import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SocialSidebar from '../components/layout/SocialSidebar';
import { companyInfo } from '../data/mockData';
import { Calendar, User, ArrowLeft, Share2, Facebook, Twitter, Linkedin, Loader2, PlayCircle } from 'lucide-react';
import api from '../services/api';

const NewsDetailPage = () => {
  const { slug } = useParams();
  const [news, setNews] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(false);
      try {
        const response = await api.news.getBySlug(slug);

        if (response.ok) {
          const data = await response.json();
          setNews(data);

          const allResponse = await api.news.getAll();
          if (allResponse.ok) {
            const allData = await allResponse.json();
            const others = allData
              .filter(n => n.id !== data.id && n.is_published)
              .slice(0, 3);
            setRelatedNews(others);
          }
        } else {
          setError(true);
        }
      } catch (err) {
        console.error(err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    window.scrollTo(0, 0);
  }, [slug]);

  const getYoutubeEmbedUrl = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? `https://www.youtube.com/embed/${match[2]}` : null;
  };

  const isVideoFile = (url) => {
    if (!url) return false;
    return url.includes('firebasestorage.googleapis.com') || url.match(/\.(mp4|webm|ogg)$/i);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#2A9D8F] animate-spin" />
      </div>
    );
  }

  if (error || !news) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-grow pt-[140px] pb-16 flex items-center justify-center">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Article Not Found</h1>
            <p className="text-gray-600 mb-8">The news article you are looking for does not exist.</p>
            <Link to="/news" className="bg-[#2A9D8F] text-white px-6 py-3 rounded font-medium hover:bg-[#238b7f] transition-colors">
              View All News
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const embedUrl = getYoutubeEmbedUrl(news.video_url);
  const isRawVideo = isVideoFile(news.video_url);

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{news.title} - {companyInfo.name}</title>
        <meta name="description" content={news.summary || news.title} />
      </Helmet>

      <Header />
      <SocialSidebar />

      <main className="pt-[140px] pb-16">
        <article className="max-w-4xl mx-auto px-4">
          <Link to="/news" className="inline-flex items-center text-[#2A9D8F] hover:underline mb-6">
            <ArrowLeft size={18} className="mr-2" />
            Back to News
          </Link>

          <header className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {news.title}
            </h1>
            <div className="flex items-center space-x-6 text-gray-500 text-sm">
              <span className="flex items-center">
                <Calendar size={16} className="mr-2" />
                {new Date(news.date || news.created_at).toLocaleDateString()}
              </span>
              <span className="flex items-center">
                <User size={16} className="mr-2" />
                {news.author || 'Admin'}
              </span>
              <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs font-medium">
                {news.category}
              </span>
            </div>
          </header>

          {news.image_url && (
            <div className="rounded-lg overflow-hidden mb-8 shadow-sm">
              <img
                src={news.image_url}
                alt={news.title}
                className="w-full max-h-[500px] object-cover"
              />
            </div>
          )}

          {/* Video Section */}
          {embedUrl && (
            <div className="rounded-lg overflow-hidden mb-8 shadow-sm aspect-video">
              <iframe
                width="100%"
                height="100%"
                src={embedUrl}
                title="Video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          )}

          {!embedUrl && isRawVideo && (
            <div className="rounded-lg overflow-hidden mb-8 shadow-sm bg-black">
              <video
                src={news.video_url}
                controls
                className="w-full max-h-[500px]"
                poster={news.image_url} // Use featured image as poster if available
              >
                Your browser does not support the video tag.
              </video>
            </div>
          )}

          {!embedUrl && !isRawVideo && news.video_url && (
            <div className="mb-8 p-4 bg-gray-100 rounded-lg flex items-center gap-2">
              <PlayCircle className="text-red-600" />
              <a href={news.video_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
                Watch Video Link
              </a>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <div
              className="prose max-w-none text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: news.content ? news.content.replace(/\n/g, '<br/>') : '' }}
            />

            <div className="border-t border-gray-200 mt-8 pt-6">
              <div className="flex items-center justify-between">
                <span className="flex items-center text-gray-600">
                  <Share2 size={18} className="mr-2" />
                  Share this article
                </span>
                <div className="flex space-x-3">
                  <button className="p-2 bg-[#3b5998] text-white rounded-full hover:opacity-80 transition-opacity">
                    <Facebook size={18} />
                  </button>
                  <button className="p-2 bg-[#1da1f2] text-white rounded-full hover:opacity-80 transition-opacity">
                    <Twitter size={18} />
                  </button>
                  <button className="p-2 bg-[#0077b5] text-white rounded-full hover:opacity-80 transition-opacity">
                    <Linkedin size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {relatedNews.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">More News</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedNews.map((item) => (
                  <Link
                    key={item.id}
                    to={`/news/${item.slug}`}
                    className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow group flex flex-col h-full"
                  >
                    <div className="h-40 overflow-hidden bg-gray-200">
                      {item.image_url ? (
                        <img
                          src={item.image_url}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <Loader2 className="w-8 h-8" />
                        </div>
                      )}
                    </div>
                    <div className="p-4 flex-grow">
                      <span className="text-xs text-gray-500">
                        {new Date(item.date || item.created_at).toLocaleDateString()}
                      </span>
                      <h3 className="font-semibold text-gray-900 line-clamp-2 mt-1 group-hover:text-[#2A9D8F] transition-colors">
                        {item.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default NewsDetailPage;
